/* import __COLOCATED_TEMPLATE__ from './widget-accounts-receivable.hbs'; */
import $ from 'jquery';
import { inject as service } from '@ember/service';
import EmberObject from '@ember/object';
import { A } from '@ember/array';
import Component from '@ember/component';
import { absoluteStoryhunterUrl } from "../../../utils/url";

export default Component.extend({
  /**
   * An object containing the title of the ageRange (for display purposes) and the corresponding filter string
   * that will produce said range with a call to the API.
   *
   * Filter strings are generally of the form (minimum days old, maximum days old), but if you omit the second value
   * it will assume you just want everything older than "minimum days old"
   *
   * Ex:
   *
   * filterString: '1,5' // will return all metaInvoices between 1 and 5 days old
   * filterString: '85'  // will return all metaInvoices older than 85 days old.
   *
   * @property ageRanges
   * @type Object
   * @default {{rangeTitle: '0 - 30 Days Old', filterString: '0,30', ... }}
   * @public
   */

  ageRanges: A([
    EmberObject.create({
      rangeTitle: '0 - 30 Days Old',
      filterString: '0,30'
    }),
    EmberObject.create({
      rangeTitle: '31 - 60 Days Old',
      filterString: '31,60'
    }),
    EmberObject.create({
      rangeTitle: '61 - 90 Days Old',
      filterString: '61,90'
    }),
    EmberObject.create({
      rangeTitle: 'Over 90 Days Old',
      filterString: '91'
    })
  ]),

  /**
   * Tracks whether or not all of the content is currently or has finished loading from the API network call(s)
   *
   * @property isLoading
   * @type Boolean
   * @default true
   * @private
   */
  isLoading: true,

  store: service(),

  init() {
    this._super(...arguments);
    this._getAgeRanges();
  },

  didInsertElement() {
    // this._generatePopIns();
  },

  /**
   * Searches the rendered view for any links that contain query strings beginning with 'id=', and replaces the links
   * with selectable <inputs> that list the id's contained in the query string.  This is intended to be a stop-gap
   * solution that allows admins to easily copy/paste the list of ID's from any generated links, until the
   * endpoint(s) for those links is/are completed.
   *
   * @method _generatePopIns
   * @private
   */
  _generatePopIns(){
    // Listen for any click events triggered by <a> elements
    $(this.element).on('click', 'a', function(e){
      let aTag = $(e.target);
      let href = aTag.attr('href');

      if(href) {
        //search for query string URL's of type "?id=..."
        let idStringMatch = /\?id=(.*)$/.exec(href);

        if(idStringMatch !== null) {
          e.preventDefault();

          //grab the comma-delimited list of ID's (ie. 1,2,3)
          let idString = decodeURIComponent(idStringMatch[1]);

          // Build a span (pop in) that will replace the a-tag with href=query string URL
          let span = $(`<span class="pop-in"><span class="id-title">ID's:</span><span class="id-string">${idString}</span><a class="close" href="">close</a></span>`);

          // Set the display of span to inline-block if the <a> tag's display value is set to "inline", otherwise just use the <a> tag's value.
          let aTagDisplayValue = aTag.css('display');
          if(aTagDisplayValue && aTagDisplayValue !== ''){
            if(aTagDisplayValue === 'inline'){
              span.css('display', 'inline-block');
            } else {
              span.css('display', aTagDisplayValue);
            }
          }

          // Configure the "close" button functionality for the pop-in
          span.on('click', 'a.close', function(e){
            e.preventDefault();
            span.replaceWith(aTag);
            span.remove();
          });

          // Automatically select the id string text if clicked on
          span.on('click', '.id-string', function(e) {
            $(this).select();
          });


          // Inject the pop-in span into the DOM in place of the <a> tag, we do this now so we can calculate its rendered width
          aTag.replaceWith(span);

          // Grab the id-string's width
          let stringWidth = span.find('.id-string').css('width');

          // Create an <input> tag, set its width to the previously calculated width, and as set its value to the id string.
          //
          // FYI:  We need to create an <input> tag in order to auto-select the text, and we need to explicitly set its
          // width in order to prevent the id string from being scrolled within the <input> (and thus invisible
          // to the user without using the arrow keys or mouse)
          span.find('.id-string').replaceWith($(`<input class="id-string" type="text" value="${idString}" />`).css('width', stringWidth));
          span.find('.id-string').select();
        }
      }
    });
  },

  /**
   * Processes the pre-defined ageRanges object and makes the corresponding call to the API to retrieve
   * invoices for each of the specified age ranges.  Once the data has been retrieved, the method updates
   * the ageRanges object to include the invoices, sets isLoading to false, and then builds the display data
   *
   * @method _getAgeRanges
   * @private
   */
  _getAgeRanges: function() {
    let self = this;
    let store = this.get('store');
    let ranges = this.get('ageRanges');

    function storeRange(rangeInvoices) {
      this.set('rangeInvoices', rangeInvoices);
    }

    function updateLoadingStatus() {
      let ranges = self.get('ageRanges');
      let isLoading = ranges.any(function(item){
        return (item.get('rangeInvoices') === undefined);
      });
      if (!self.isDestroying) {
        self.set('isLoading', isLoading);
      }
    }

    function buildDisplayDataByCompany() {
      if(self.get('isLoading') === false){
        self._buildDisplayDataByCompany();
      }
    }

    ranges.forEach((item) => {
      store.query('shInvoice', {
        include: 'company',
        unpaged: 1,
        filter: {
          ageRange: item.get('filterString'),
          sent_status: 1,
          paid: 0,
          final_status: 1,
          is_child: 0,
        }
      }).then(storeRange.bind(item)).then(updateLoadingStatus).then(buildDisplayDataByCompany);
    });

  },

  /**
   * Grabs all of the invoices per ageRange (as specified in the ageRanges object) and categorizes the info
   * by Company.  Performs many summing calculations and ID organization for convenient reference in the template
   *
   * @method _buildDisplayDataByCompany
   * @private
   */
  _buildDisplayDataByCompany() {

    function _generateIdString(idString, metaId){
      return idString += (idString === '') ? `${metaId}` : `,${metaId}`;
    }

    let ranges = this.get('ageRanges');
    let displayDataByCompany = EmberObject.create({
      ageRangeHeaders: A(),  // Titles for each age range
      ageRangeTotals: A(),   // Sum of invoices for each age range
      ageRangeInvoiceIdStrings: A(), // Comma-delimited string of id's for each age range
      ageRangeInvoiceIds: A(), // Array of ID's for each age range
      allInvoiceTotal: 0, // All of the invoices totaled (every invoice from each range summed up into one total)
      data: EmberObject.create(), // Age range data broken up by company
    });

    ranges.forEach(function(range, rangeIndex){
      let ageRangeHeaders = displayDataByCompany.get('ageRangeHeaders');
      let ageRangeTotals = displayDataByCompany.get('ageRangeTotals');
      let ageRangeInvoiceIdStrings = displayDataByCompany.get('ageRangeInvoiceIdStrings');
      let ageRangeInvoiceIds = displayDataByCompany.get('ageRangeInvoiceIds');
      let allInvoiceTotal = displayDataByCompany.get('allInvoiceTotal');
      let displayData = displayDataByCompany.get('data');

      ageRangeHeaders.push(range.get('rangeTitle')); // grab range header from ageRange object
      ageRangeTotals.push(0); // initialize total to 0
      ageRangeInvoiceIdStrings.push('');
      ageRangeInvoiceIds.push(A());

      range.get('rangeInvoices').forEach(function(invoice){
        let companyId = invoice.get('company.id');
        let metaId = invoice.get('id');

        // Initialize the data object for each Company Id if it hadn't already been set
        if(displayData.get(companyId) === undefined) {
          displayData.set(companyId, EmberObject.create({
            companyName: invoice.get('company.name'),
            allInvoiceSum: 0,
            allInvoiceIdString: '',
            allInvoiceIds: A(),
            invoiceRanges: (() => {
              let array = A(new Array(ranges.length));
              for (let i=0; i < array.length; i++) {
                array[i] = EmberObject.create({
                  invoiceSum: 0,
                  invoiceIdString: '',
                  invoiceIds: A(),
                  invoices: A()
                });
              }
              return array;
            })()
          }));
        }

        let company = displayData.get(companyId);

        ageRangeTotals[rangeIndex] += parseFloat(invoice.get('unpaidAmount'));
        ageRangeInvoiceIdStrings[rangeIndex] = _generateIdString(ageRangeInvoiceIdStrings[rangeIndex], metaId);
        ageRangeInvoiceIds[rangeIndex].push(metaId);

        let allInvoiceSum = company.get('allInvoiceSum');
        let allInvoiceIdString = company.get('allInvoiceIdString');
        let allInvoiceIds = company.get('allInvoiceIds');

        allInvoiceSum += parseFloat(invoice.get('unpaidAmount'));
        allInvoiceIdString = _generateIdString(allInvoiceIdString, metaId);

        company.set('allInvoiceSum', allInvoiceSum);
        company.set('allInvoiceIdString', allInvoiceIdString);
        allInvoiceIds.push(metaId);

        let invoiceRange = company.get('invoiceRanges')[rangeIndex];

        let invoiceSum = invoiceRange.get('invoiceSum');
        let invoiceIdString = invoiceRange.get('invoiceIdString');
        let invoiceIds = invoiceRange.get('invoiceIds');
        let invoices = invoiceRange.get('invoices');

        invoiceSum += parseFloat(invoice.get('unpaidAmount'));
        invoiceIdString = _generateIdString(invoiceIdString, metaId);

        invoiceRange.set('invoiceSum', invoiceSum);
        invoiceRange.set('invoiceIdString', invoiceIdString);
        invoiceIds.push(metaId);
        invoices.push(invoice);

      });

      allInvoiceTotal += ageRangeTotals[rangeIndex];
      displayDataByCompany.set('allInvoiceTotal', allInvoiceTotal);
    });

    this.set('displayDataByCompany', displayDataByCompany);
  },

  actions: {
    /**
     * Trigger the admin report csv download.
     */
    onDownloadCSVClicked() {
      window.open(absoluteStoryhunterUrl('/sh-invoices/admin-report'));
    }
  }
});
